"use strict";
/**
 * Direct Peritext `Fragment` to Slate `Descendant[]` converter with caching.
 *
 * Implements a double-buffered cache keyed by Peritext `Block.hash`, which lets
 * unchanged sub-trees be reused across renders so that React's reconciler can
 * skip re-rendering unchanged components.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ToSlateNode = void 0;
const Slice_1 = require("json-joy/lib/json-crdt-extensions/peritext/slice/Slice");
const constants_1 = require("json-joy/lib/json-crdt-extensions/peritext/slice/constants");
const peritext_1 = require("json-joy/lib/json-crdt-extensions/peritext");
const isText = (node) => typeof node.text === 'string';
/**
 * Double-buffered cache for Slate element nodes, keyed by Peritext Block hash.
 * Entries that are not accessed during the current render pass are dropped at
 * the next {@link SlateNodeCache.gc} call.
 */
class SlateNodeCache {
    constructor() {
        /** Entries written/read during the *current* render pass. */
        this.curr = new Map();
        /** Entries from the *previous* render pass (read-only during current). */
        this.prev = new Map();
    }
    get(hash) {
        const curr = this.curr;
        let node = curr.get(hash);
        if (node !== undefined)
            return node;
        node = this.prev.get(hash);
        if (node !== undefined)
            curr.set(hash, node);
        return node;
    }
    set(hash, node) {
        this.curr.set(hash, node);
    }
    /** Call once at the **end** of each render pass. Drops entries not accessed
     * during this render. */
    gc() {
        this.prev = this.curr;
        this.curr = new Map();
    }
}
/**
 * Stateful converter that turns a Peritext `Fragment` into a Slate
 * `Descendant[]` document, reusing unchanged sub-trees via {@link SlateNodeCache}.
 *
 * Because unchanged blocks return the **same object reference**, React's
 * reconciler (and Slate's `onChange` diffing) can skip re-rendering them.
 */
class ToSlateNode {
    constructor(defaultBlock = 'p') {
        this.defaultBlock = defaultBlock;
        this.cache = new SlateNodeCache();
    }
    convert(fragment) {
        const root = fragment.root;
        const blockChildren = root.children;
        const length = blockChildren.length;
        const result = [];
        const cache = this.cache;
        for (let i = 0; i < length; i++) {
            const block = blockChildren[i];
            const cached = cache.get(block.hash);
            if (cached) {
                result.push(cached);
                continue;
            }
            const node = this.convBlock(block);
            result.push(node);
        }
        cache.gc();
        return result;
    }
    convBlock(block) {
        const hash = block.hash;
        const cached = this.cache.get(hash);
        if (cached)
            return cached;
        const node = this.buildBlock(block);
        this.cache.set(hash, node);
        return node;
    }
    buildBlock(block) {
        if (block instanceof peritext_1.LeafBlock) {
            const inlineChildren = [];
            for (let iterator = block.texts0(), inline; (inline = iterator());) {
                const text = inline.text();
                const attr = inline.attr();
                const attrKeys = Object.keys(attr);
                // Detect an `Atomic` slice — this run represents an inline void
                // element. Emit it as a Slate inline element and skip the placeholder character.
                let atomicSlice;
                let atomicTag;
                for (const tag of attrKeys) {
                    const stack = attr[tag];
                    if (!stack || stack.length <= 0)
                        continue;
                    const slice = stack[0].slice;
                    if (!(slice instanceof Slice_1.Slice))
                        continue;
                    if (slice.stacking === constants_1.SliceStacking.Atomic) {
                        atomicSlice = slice;
                        atomicTag = tag;
                        break;
                    }
                }
                if (atomicSlice && atomicTag !== undefined) {
                    const data = atomicSlice.data();
                    const inlineEl = {
                        type: atomicTag,
                        children: [{ text: '' }],
                    };
                    if (data && typeof data === 'object' && !Array.isArray(data))
                        Object.assign(inlineEl, data);
                    inlineChildren.push(inlineEl);
                    continue;
                }
                if (!text && attrKeys.length === 0)
                    continue;
                const textNode = { text: text || '' };
                const length = attrKeys.length;
                ATTRS: for (let i = 0; i < length; i++) {
                    const tag = attrKeys[i];
                    const stack = attr[tag];
                    if (!stack || stack.length <= 0)
                        continue ATTRS;
                    const slice = stack[0].slice;
                    if (!(slice instanceof Slice_1.Slice))
                        continue ATTRS;
                    if (slice.stacking === constants_1.SliceStacking.Atomic)
                        continue ATTRS;
                    const data = slice.data();
                    if (data && typeof data === 'object' && !Array.isArray(data))
                        Object.assign(textNode, { [tag]: data });
                    else
                        textNode[tag] = data !== undefined ? data : true;
                }
                inlineChildren.push(textNode);
            }
            // Slate requires text nodes around inline elements: first and last
            // child must be text, and two inline elements can't be adjacent.
            if (inlineChildren.length === 0) {
                inlineChildren.push({ text: '' });
            }
            else {
                if (!isText(inlineChildren[0]))
                    inlineChildren.unshift({ text: '' });
                if (!isText(inlineChildren[inlineChildren.length - 1]))
                    inlineChildren.push({ text: '' });
                for (let i = 1; i < inlineChildren.length; i++) {
                    if (!isText(inlineChildren[i]) && !isText(inlineChildren[i - 1])) {
                        inlineChildren.splice(i, 0, { text: '' });
                        i++;
                    }
                }
            }
            const tag = block.tag();
            const node = {
                type: tag === 0 ? this.defaultBlock : tag + '',
                children: inlineChildren,
            };
            const attr = block.attr();
            if (attr && typeof attr === 'object')
                Object.assign(node, attr);
            return node;
        }
        else {
            const childBlocks = block.children;
            const len = childBlocks.length;
            const children = new Array(len);
            for (let i = 0; i < len; i++)
                children[i] = this.convBlock(childBlocks[i]);
            const attr = block.attr();
            const tag = block.tag();
            const node = {
                ...(attr && typeof attr === 'object' ? attr : {}),
                type: tag === 0 ? this.defaultBlock : tag + '',
                children: len ? children : [{ text: '' }],
            };
            return node;
        }
    }
}
exports.ToSlateNode = ToSlateNode;
